import { useCurrentUser } from "@/contexts";
import { isImpersonating } from "@/utils/auth/impersonation";

/**
 * Whether this session may offer impersonation at all (target-independent).
 *
 * Impersonation is a **superAdmin-only** capability: only a superAdmin may mint
 * an impersonation token, and they may target any non-superAdmin user. Every
 * other caller is refused with a `403` by the backend, so the action must be
 * absent rather than present-and-failing. The role comes from the app's
 * established identity source — `useCurrentUser()` (`CurrentUserContext`, fed by
 * `GET /users/me`) — the same signal `SuperAdminGuard` checks; it is deliberately
 * *not* the platform `USER:IMPERSONATE` capability, which is granted more
 * broadly than this rule and would offer the action to platform managers.
 *
 * It is also hidden inside an already-impersonated tab: the backend refuses
 * chained impersonation, so offering it there is a guaranteed error.
 * `isImpersonating()` is read, not subscribed to — the session is seeded from
 * the URL fragment before React renders and can only end by a full reload
 * (`useImpersonationSession().endSession`), so there is no changing state to
 * track and no per-row timer to pay for.
 *
 * Session-wide and target-independent: it lives here, next to the other user
 * permission hooks, rather than beside a table's row-actions hook, because it is
 * the single source of truth every impersonation entry point (users table,
 * global search) must gate on.
 */
export const useCanImpersonateUsers = (): boolean => {
  const currentUser = useCurrentUser();

  return currentUser.role === "superAdmin" && !isImpersonating();
};
